import { Pipe, PipeTransform } from '@angular/core';
import { differenceInCalendarDays, format, formatDistanceToNowStrict } from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';

/** date-fns locales keyed by the app's language codes; falls back to English. */
const LOCALES: Record<string, typeof enUS> = { en: enUS, fr, es };

/** Older than this many days → show an absolute date instead of a relative "x ago". */
const RELATIVE_THRESHOLD_DAYS = 7;

/**
 * Formats a date as relative time ("3 minutes ago") for recent values, falling back to an
 * absolute, localized date once it is older than {@link RELATIVE_THRESHOLD_DAYS}.
 *
 * Usage: `{{ value | timeAgo: currentLang() }}`
 */
@Pipe({ name: 'timeAgo' })
export class TimeAgoPipe implements PipeTransform {
  // `_tick` is never read: a changing argument is what makes Angular re-run this pure pipe.
  transform(
    value: string | number | Date | null | undefined,
    lang?: string,
    _tick?: number
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }

    const locale = LOCALES[lang ?? 'en'] ?? enUS;
    const daysSince = Math.abs(differenceInCalendarDays(new Date(), date));

    return daysSince >= RELATIVE_THRESHOLD_DAYS
      ? format(date, 'PPp', { locale })
      : formatDistanceToNowStrict(date, { addSuffix: true, locale });
  }
}
